import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Building2, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { RootState } from "configureStore";
import type { CompanyGroup, GroupCompany } from "types/CompanyGroup";

type Company = {
  id: string;
  name: string;
  role?: string;
};

interface CompanySelectorProps {
  companyId?: string;
  selectedCompanyLabel?: string;
  onCompanyChange: (id: string) => void;
  /** Opens a group company the person is not a member of (news and "About" only). */
  onVisitGroupCompany: (id: string) => void;
}

export function CompanySelector({
  companyId,
  selectedCompanyLabel,
  onCompanyChange,
  onVisitGroupCompany,
}: CompanySelectorProps) {
  const { t } = useTranslation();
  const { companiesCollectionRaw = [], companyGroups = [] } =
    useSelector((state: any) => state.companySelectReducer) || {};
  const companies: Company[] = companiesCollectionRaw || [];
  // Companies of the person's groups that they can open but are not a member of.
  const visitableCompanies: GroupCompany[] = (companyGroups as CompanyGroup[])
    .flatMap((group) => group.companies)
    .filter((company) => !company.isMember);
  const unreadByCompany = useSelector(
    (state: RootState) => state.chatReducer.unreadByCompany || {}
  );
  // Total unread across all OTHER companies — for the collapsed selector button.
  const otherCompaniesUnread = useMemo(() => {
    let sum = 0;
    for (const id of Object.keys(unreadByCompany)) {
      if (id === companyId) continue;
      sum += unreadByCompany[id]?.count || 0;
    }
    return sum;
  }, [unreadByCompany, companyId]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasCompanies = companies.length > 0 || visitableCompanies.length > 0;

  const selectedCompany = useMemo(() => {
    if (companyId) {
      return companies.find((company) => company.id === companyId);
    }
    return undefined;
  }, [companyId, companies]);

  const visitedCompany = visitableCompanies.find(
    (company) => company.id === companyId
  );

  const buttonLabel = hasCompanies
    ? selectedCompany?.name ||
      visitedCompany?.name ||
      selectedCompanyLabel ||
      "Select Company"
    : "No companies available";

  const isDisabled = !hasCompanies;

  useEffect(() => {
    // pointerdown, not mousedown: iOS sends no mouse events for a tap on plain text.
    function handleClickOutside(event: PointerEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("pointerdown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  useEffect(() => {
    setIsDropdownOpen(false);
  }, [companyId]);

  return (
    <div
      className="dashboard-topbar-slot flex items-center border-b border-white/10 px-4"
      ref={containerRef}
    >
      <div className="relative w-full">
        <button
          type="button"
          onClick={() => {
            if (!isDisabled) {
              setIsDropdownOpen((open) => !open);
            }
          }}
          className={`w-full bg-white/10 hover:bg-white/15 px-3 py-2 flex items-center justify-between transition-colors border-0 ${
            isDisabled ? "text-white/40 cursor-not-allowed" : "text-white"
          }`}
          style={{ borderRadius: "var(--radius-sm)" }}
          aria-haspopup="listbox"
          aria-expanded={isDropdownOpen}
          disabled={isDisabled}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm truncate">{buttonLabel}</span>
            {otherCompaniesUnread > 0 && (
              <span
                className="ml-auto bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight"
                title={`${otherCompaniesUnread} unread in other companies`}
              >
                {otherCompaniesUnread > 99 ? "99+" : otherCompaniesUnread}
              </span>
            )}
          </div>
          <ChevronDown
            className={`w-4 h-4 flex-shrink-0 transition-transform ${
              isDropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isDropdownOpen && hasCompanies && (
          <div
            className="absolute left-0 right-0 top-full mt-1 bg-white shadow-lg border border-gray-200 py-1 z-50 max-h-64 overflow-y-auto"
            style={{ borderRadius: "var(--radius-sm)" }}
          >
            {companies.map((company) => (
              <CompanyRow
                key={company.id}
                name={company.name}
                role={company.role}
                isActive={company.id === companyId}
                unread={company.id === companyId ? 0 : unreadByCompany[company.id]?.count || 0}
                onClick={() => {
                  onCompanyChange(company.id);
                  setIsDropdownOpen(false);
                }}
              />
            ))}

            {visitableCompanies.length > 0 && (
              <>
                <div className="px-3 pt-3 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t("company_selector_group_companies")}
                </div>
                {visitableCompanies.map((company) => (
                  <CompanyRow
                    key={company.id}
                    name={company.name}
                    isActive={company.id === companyId}
                    onClick={() => {
                      onVisitGroupCompany(company.id);
                      setIsDropdownOpen(false);
                    }}
                  />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface CompanyRowProps {
  name: string;
  /** The person's role in the company, when it is known. */
  role?: string;
  /** The company currently open. */
  isActive: boolean;
  /** Unread chat messages in the company; the badge shows only above zero. */
  unread?: number;
  onClick: () => void;
}

function CompanyRow({ name, role, isActive, unread = 0, onClick }: CompanyRowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${
        isActive ? "bg-gray-100" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-gray-600 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-gray-900 truncate">{name}</div>
          {role && <div className="text-xs text-gray-500">{role}</div>}
        </div>
        {unread > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-tight">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </div>
    </button>
  );
}
